/**
 * TypeScript type definitions for Record Mapping JSON Schema
 * Used in DCRepresentativeImport.recordMapping property
 */

import type { SelectDropdownItem } from './Base';

// Mapping type enumeration
export enum MappingType {
  CSV = 1,
  FixedLength = 2,
  JSON = 3,
  Database = 4,
  RestApi = 5,
  XLXS = 6,
}

// Main Record Mapping Schema
export interface RecordMappingSchema {
  MappingInfo: MappingInfo;
  ImportSettings: ImportSettings;
  Mappings: RecordMapping[];
}

// Mapping Info Section
export interface MappingInfo {
  MappingType: MappingType;
  SkipFirstLine: number;
  Description: string;
  CreatedDate: string;
  RepresentativeID: string;
  CompanyID: string;
  CompanySiteID: string;
  ConcatenationSettings: ConcatenationSettings;
}

// Concatenation Settings
export interface ConcatenationSettings {
  DefaultSeparator: string;
  AllowCustomSeparators: boolean;
  IncludeSeparatorInSource: boolean;
  Description: string;
}

// Import Settings Container
export interface ImportSettings {
  CSV: CSVImportSettings;
  FixedLength: FixedLengthImportSettings;
  JSON: JSONImportSettings;
  Database: DatabaseImportSettings;
  RestApi: RestApiImportSettings;
  XLXS: XLXSImportSettings;
}

// CSV Import Settings
export interface CSVImportSettings {
  Separator: string;
  QuoteCharacter: string;
  Encoding: string;
}

// Fixed Length Import Settings
export interface FixedLengthImportSettings {
  RecordLength: number | null;
  PadCharacter: string;
}

// JSON Import Settings
export interface JSONImportSettings {
  RootPath: string;
  ArrayPath: string;
  Encoding: string;
  ValidateSchema: boolean;
}

// Database Import Settings
export interface DatabaseImportSettings {
  Query: string;
  ConnectionString: string;
  CommandTimeout: number | null;
}

// RestApi Import Settings

export interface RestApiImportSettings {}

// XLXS Import Settings
export interface XLXSImportSettings {}

// Record Mapping Definition
export interface RecordMapping {
  RecordType: string;
  TargetEntity: string;
  Description: string;
  FieldMappings: FieldMapping[];
}

// Field Mapping Definition (can be nested)
export interface FieldMapping {
  TargetField: string;
  IsRequired: boolean | undefined;
  IsNullable: boolean | undefined;
  DataType: string;
  DefaultValue: string | undefined;
  Description: string;
  IsChildCollection: boolean;
  ChildRecordType: string;
  MappingFields: MappingField[];
  LookupSettings: LookupSettings;
  ChildMappingSettings: ChildMappingSettings | null;
  ChildFieldMappings: FieldMapping[] | null;
  // UI state for BaseSearchDropdown v-model binding
  selectedTargetFieldItem?: SelectDropdownItem | null;
  selectedDataType?: SelectDropdownItem | null;
}

// Mapping Field Container
export interface MappingField {
  CSV: CSVMappingField;
  FixedLength: FixedLengthMappingField;
  JSON: JSONMappingField;
  Database: DatabaseMappingField;
  RestApi?: RestApiMappingField;
  XLXS?: XLXSMappingField;
}

// Mapping Field Sources
export interface CSVMappingField {
  ColumnIndex: number | null;
  ColumnName: string;
}

export interface FixedLengthMappingField {
  StartPosition: number | null;
  Length: number | null;
}

export interface JSONMappingField {
  JsonPath: string;
}

export interface DatabaseMappingField {
  ColumnName: string;
}

export interface RestApiMappingField {}

export interface XLXSMappingField {
  ColumnName: string;
}

// Lookup Settings
export interface LookupSettings {
  IsEnabled: boolean | null;
  LookupTable: string;
  LookupKeyField: string;
  LookupResultField: string;
  FilterConditions?: FilterCondition[];
  LookupCacheResults?: boolean | null;
  LookupCacheTimeout?: number | null;
  LookupDescription: string;
  ToValidate: ToValidateSettings;
}

// Filter Condition for Lookup Settings
export interface FilterCondition {
  Field: string;
  Operator: string;
  Value: string;
}

export interface ToValidateSettings {
  IsEnabled: boolean;
  LogTableName: string;
  SourceField: string;
}

export interface ChildMappingSettings {
  RelationshipKey: string;
  ToTable: ToTableSettings;
  CSV: CSVChildMappingSettings;
  FixedLength: FixedLengthChildMappingSettings;
  JSON: JSONChildMappingSettings;
  Database: DatabaseChildMappingSettings;
  RestApi: RestApiChildMappingSettings;
  XLXS: XLXSChildMappingSettings;
}

// Child Mapping Settings for nested/related data
export interface ToTableSettings {
  TableName: string;
  KeyField: string;
}

export interface CSVChildMappingSettings {
  StartColumnIndex: number | null;
  DetailSeparator: string;
  FieldSeparator: string;
}

export interface FixedLengthChildMappingSettings {
  StartPosition: number | null;
  DetailRecordLength: number | null;
  MaxDetails: number | null;
}

export interface JSONChildMappingSettings {
  ArrayPath: string;
  ItemPath: string;
}

export interface DatabaseChildMappingSettings {
  JoinCondition: string;
  GroupBy: string;
}

export interface RestApiChildMappingSettings {}

export interface XLXSChildMappingSettings {}

// Type guard functions for runtime validation
export const isValidMappingType = (type: number): type is MappingType => {
  return [
    MappingType.CSV,
    MappingType.FixedLength,
    MappingType.JSON,
    MappingType.Database,
    MappingType.RestApi,
    MappingType.XLXS,
  ].includes(type);
};

// Validation function for the entire schema
export const validateRecordMappingSchema = (data: any): data is RecordMappingSchema => {
  try {
    if (!data || typeof data !== 'object') return false;

    if (!data.MappingInfo || !data.ImportSettings || !Array.isArray(data.Mappings)) return false;

    const mappingInfo = data.MappingInfo;
    if (!mappingInfo.MappingType || !isValidMappingType(mappingInfo.MappingType)) return false;

    return true;
  } catch (error) {
    return false;
  }
};

// Data Collection Entity types for API integration
export interface DataCollectionProperty {
  propertyName: string;
  isChildCollection: boolean;
}

export interface DataCollectionEntity {
  tableName: string;
  entityName: string;
  properties: DataCollectionProperty[];
}

export enum eDataTypes {
  string = 1,
  date,
  dateTime,
  boolean,
  integer,
  decimal,
  currency,
}
